import React, { useState, useEffect, useMemo } from "react";
import { apiClient } from "../utils/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { format, isToday, isYesterday, startOfDay } from "date-fns";

const actionConfig = {
  created: {
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
  },
  updated: {
    badge: "bg-blue-50 text-blue-700 border-blue-200",
    dot: "bg-blue-500",
  },
  deleted: {
    badge: "bg-red-50 text-red-700 border-red-200",
    dot: "bg-red-500",
  },
  converted: {
    badge: "bg-violet-50 text-violet-700 border-violet-200",
    dot: "bg-violet-500",
  },
  reminded: {
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
  },
};

const entityConfig = {
  lead: { icon: "👤", label: "Lead" },
  meeting: { icon: "📅", label: "Meeting" },
  client: { icon: "🏢", label: "Client" },
  shoot: { icon: "📸", label: "Shoot" },
  editing_task: { icon: "✂️", label: "Editing Task" },
  ad: { icon: "📢", label: "Ad" },
  user: { icon: "👤", label: "User" },
  role: { icon: "🔒", label: "Role" },
};

const ALL_ACTIONS = ["all", "created", "updated", "deleted", "converted"];

function groupByDate(logs) {
  const groups = {};
  for (const log of logs) {
    const d = startOfDay(new Date(log.created_at)).toISOString();
    if (!groups[d]) groups[d] = [];
    groups[d].push(log);
  }
  return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
}

function dateLabel(isoDate) {
  const d = new Date(isoDate);
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "EEEE, MMMM d, yyyy");
}

function LogSkeleton() {
  return (
    <div className="flex gap-4 py-3">
      <Skeleton className="w-9 h-9 rounded-full shrink-0" />
      <div className="flex-1 space-y-2 pt-0.5">
        <div className="flex gap-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-16 rounded-full" />
          <Skeleton className="h-4 w-20 rounded-full" />
        </div>
        <Skeleton className="h-3.5 w-64" />
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
  );
}

export default function ActivityLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await apiClient.get("/activity-logs");
      setLogs(response.data);
    } catch {
      toast.error("Failed to load activity logs");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let result = logs;
    if (activeFilter !== "all")
      result = result.filter((l) => l.action === activeFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) =>
          l.user_name.toLowerCase().includes(q) ||
          l.details.toLowerCase().includes(q) ||
          l.entity_type.toLowerCase().includes(q),
      );
    }
    return result;
  }, [logs, activeFilter, search]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  return (
    <div className="space-y-6" data-testid="activity-logs-page">
      {/* ── Header ── */}
      <div>
        <h1
          className="text-3xl lg:text-4xl font-bold"
          style={{ letterSpacing: "-0.02em" }}
        >
          Activity Log
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Complete audit trail of all system activities
        </p>
      </div>

      {/* ── Filters + Search ── */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Filter tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg">
          {ALL_ACTIONS.map((action) => (
            <button
              key={action}
              onClick={() => setActiveFilter(action)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-all duration-150
                ${
                  activeFilter === action
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
            >
              {action}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search logs…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ── Timeline ── */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-1 divide-y divide-border">
            {Array.from({ length: 8 }).map((_, i) => (
              <LogSkeleton key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <Activity className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="font-medium text-muted-foreground">
              {search || activeFilter !== "all"
                ? "No matching logs"
                : "No activity yet"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border fade-in">
            {grouped.map(([dateKey, dayLogs]) => (
              <div key={dateKey}>
                {/* Date header */}
                <div className="sticky top-0 z-10 bg-muted/60 backdrop-blur-sm px-6 py-2 border-b border-border">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                    {dateLabel(dateKey)}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground/60">
                    {dayLogs.length} event{dayLogs.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* Logs for this day */}
                <div className="relative px-6">
                  {/* Vertical timeline line */}
                  <div
                    className="absolute left-[2.75rem] top-0 bottom-0 w-px bg-border"
                    aria-hidden
                  />

                  {dayLogs.map((log, idx) => {
                    const ac = actionConfig[log.action] ?? {
                      badge: "bg-slate-100 text-slate-600 border-slate-200",
                      dot: "bg-slate-400",
                    };
                    const ec = entityConfig[log.entity_type] ?? {
                      icon: "📝",
                      label: log.entity_type,
                    };
                    return (
                      <div
                        key={log.id}
                        className={`flex gap-4 py-4 ${idx !== dayLogs.length - 1 ? "" : ""}`}
                      >
                        {/* Avatar dot */}
                        <div className="relative z-10 w-9 h-9 rounded-full bg-background border-2 border-border flex items-center justify-center text-base shrink-0">
                          {ec.icon}
                        </div>

                        {/* Content */}
                        <div className="flex-1 pt-1.5 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-semibold text-sm">
                              {log.user_name}
                            </span>
                            <Badge
                              className={`text-xs border ${ac.badge} flex items-center gap-1`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${ac.dot}`}
                              />
                              {log.action}
                            </Badge>
                            <Badge
                              variant="outline"
                              className="text-xs capitalize"
                            >
                              {ec.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1 leading-relaxed">
                            {log.details}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground/70">
                            <span>
                              {format(new Date(log.created_at), "h:mm a")}
                            </span>
                            <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-[10px]">
                              {log.entity_id.substring(0, 8)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
